import React, { useState, useRef } from "react";
import {
  createCampaign,
  uploadCampaignContacts,
  AgentSummary,
  CampaignCreate,
  updateCampaign,
  startCampaign,
  SuccessMetric,
  AnswerType,
  ChartType,
  MetricPriority,
  MetricCategory,
  getCampaigns,
  Campaign,
} from "../../services/api";
import {
  HiOutlineUpload,
  HiCheck,
  HiExclamation,
  HiChevronLeft,
  HiChevronRight,
  HiDocumentText,
  HiPlus,
  HiTrash,
  HiSearch,
  HiChevronDown,
  HiX,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";
import CustomMetricModal from "./CustomMetricModal";

interface CreateCampaignProps {
  agents: AgentSummary[];
  onSuccess: () => void;
  onCancel: () => void;
}

interface ContactPreview {
  phone_number: string;
  first_name: string;
  last_name?: string;
  [key: string]: string | undefined;
}

interface UploadResult {
  success: boolean;
  validContacts: number;
  errors: string[];
  preview: ContactPreview[];
}

// Common US timezones
const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona Time (MST)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
];

// Generate time options for dropdowns
const generateTimeOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const time = `${h}:${m}`;
      const label = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push({ value: time, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CreateCampaign: React.FC<CreateCampaignProps> = ({
  agents,
  onSuccess,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 1: Campaign Basics
  const [campaignName, setCampaignName] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [description, setDescription] = useState("");
  const [startOption, setStartOption] = useState("scratch");

  // Step 2: Contact Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [contactsUploaded, setContactsUploaded] = useState(false);

  // Step 3: Success Metrics Configuration
  const [selectedMetrics, setSelectedMetrics] = useState<SuccessMetric[]>([]);
  const [customMetrics, setCustomMetrics] = useState<SuccessMetric[]>([]); // All custom metrics created
  const [showCustomMetricModal, setShowCustomMetricModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "ai-recommended" | "standard">("all");

  // Standard metrics for the Standard tab
  const standardMetrics: SuccessMetric[] = [
    {
      question: "How satisfied was the customer with the call?",
      slug: "customer-satisfaction",
      answer_type: AnswerType.SCALE_5,
      category: MetricCategory.SATISFACTION,
      priority: MetricPriority.HIGH,
      reason: "Measure customer satisfaction to improve service quality",
      chart_type: ChartType.GAUGE,
      options: ["Very Dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very Satisfied"],
      config: {
        min: 1,
        max: 5,
        labels: ["Very Dissatisfied", "Very Satisfied"]
      },
      extraction_hints: "satisfaction, happy, pleased, disappointed, frustrated, rating, experience"
    },
    {
      question: "Was the customer's issue resolved?",
      slug: "issue-resolved",
      answer_type: AnswerType.YES_NO,
      category: MetricCategory.RESOLUTION,
      priority: MetricPriority.HIGH,
      reason: "Track resolution rate to measure effectiveness",
      chart_type: ChartType.PIE,
      extraction_hints: "resolved, fixed, solved, completed, unresolved, pending, escalated"
    },
    {
      question: "Would the customer recommend our service?",
      slug: "nps-recommendation",
      answer_type: AnswerType.NPS,
      category: MetricCategory.SATISFACTION,
      priority: MetricPriority.HIGH,
      reason: "Net Promoter Score indicates customer loyalty",
      chart_type: ChartType.GAUGE,
      config: {
        min: 0,
        max: 10,
        labels: ["Not at all likely", "Extremely likely"]
      },
      extraction_hints: "recommend, refer, suggest, tell others, word of mouth, likelihood, rating"
    },
    {
      question: "How engaged was the customer during the call?",
      slug: "customer-engagement",
      answer_type: AnswerType.SCALE_5,
      category: MetricCategory.ENGAGEMENT,
      priority: MetricPriority.MEDIUM,
      reason: "Measure customer engagement to optimize interaction",
      chart_type: ChartType.BAR,
      options: ["Not Engaged", "Slightly Engaged", "Moderately Engaged", "Engaged", "Highly Engaged"],
      config: {
        min: 1,
        max: 5,
        labels: ["Not Engaged", "Highly Engaged"]
      },
      extraction_hints: "engaged, interested, attentive, responsive, distracted, passive, active participation"
    },
    {
      question: "Did the customer need follow-up assistance?",
      slug: "follow-up-needed",
      answer_type: AnswerType.YES_NO,
      category: MetricCategory.FOLLOW_UP,
      priority: MetricPriority.MEDIUM,
      reason: "Track follow-up needs for resource planning",
      chart_type: ChartType.PIE,
      extraction_hints: "follow up, callback, additional help, more information, schedule, next steps"
    },
    {
      question: "How long was the customer on hold?",
      slug: "hold-duration",
      answer_type: AnswerType.DURATION_IN_MINUTES,
      category: MetricCategory.SETUP,
      priority: MetricPriority.LOW,
      reason: "Monitor hold times to improve efficiency",
      chart_type: ChartType.METRIC,
      config: {
        min: 0
      },
      extraction_hints: "hold, waiting, wait time, on hold, please hold, transfer"
    },
    {
      question: "What was the customer's emotional state?",
      slug: "customer-sentiment",
      answer_type: AnswerType.SENTIMENT,
      category: MetricCategory.ENGAGEMENT,
      priority: MetricPriority.MEDIUM,
      reason: "Track emotional state to improve customer experience",
      chart_type: ChartType.PIE,
      extraction_hints: "happy, sad, angry, frustrated, calm, excited, upset, pleased, annoyed"
    },
    {
      question: "Did the customer make a purchase or take action?",
      slug: "conversion-action",
      answer_type: AnswerType.YES_NO,
      category: MetricCategory.OUTCOME,
      priority: MetricPriority.HIGH,
      reason: "Measure conversion and action rates",
      chart_type: ChartType.PIE,
      extraction_hints: "purchase, buy, order, sign up, subscribe, agree, accept, decline, conversion"
    },
    {
      question: "Did the customer request to reschedule the call or appointment?",
      slug: "call-reschedule-requested",
      answer_type: AnswerType.YES_NO,
      category: MetricCategory.FOLLOW_UP,
      priority: MetricPriority.MEDIUM,
      reason: "Track reschedule requests to optimize scheduling and resource allocation",
      chart_type: ChartType.PIE,
      extraction_hints: "reschedule, postpone, different time, another day, change appointment, move meeting, later, tomorrow, next week"
    }
  ];

  // Step 4: Campaign Schedule
  const [scheduleStartOption, setScheduleStartOption] = useState<"immediate" | "scheduled">("immediate");
  const [scheduledDateTime, setScheduledDateTime] = useState("");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [activeDays, setActiveDays] = useState<string[]>(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [maxCallsPerDay, setMaxCallsPerDay] = useState(5000);
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState(10);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [retryEnabled, setRetryEnabled] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [retryIntervalMinutes, setRetryIntervalMinutes] = useState(60);

  // Campaign-level memory states
  const [campaignLevelMemoryEnabled, setCampaignLevelMemoryEnabled] = useState(false);
  const [previousCampaignId, setPreviousCampaignId] = useState<string | null>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignSearchTerm, setCampaignSearchTerm] = useState("");
  const [isCampaignDropdownOpen, setIsCampaignDropdownOpen] = useState(false);
  const campaignDropdownRef = useRef<HTMLDivElement>(null);

  // Step 5: Review
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);

  // Close campaign dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(event.target as Node)) {
        setIsCampaignDropdownOpen(false);
        setCampaignSearchTerm(""); // Reset search term when closing
      }
    };

    if (isCampaignDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCampaignDropdownOpen]);

  // Fetch available campaigns on mount
  React.useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        const response = await getCampaigns(1000, 0); // Fetch up to 1000 campaigns
        setAvailableCampaigns(response.campaigns);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Reset campaign-level memory when agent memory is disabled
  React.useEffect(() => {
    if (!memoryEnabled && campaignLevelMemoryEnabled) {
      setCampaignLevelMemoryEnabled(false);
      setPreviousCampaignId(null);
    }
  }, [memoryEnabled]);

  const saveCampaignAsDraft = async () => {
    try {
      // Prepare campaign data
      const campaignData: CampaignCreate = {
        name: campaignName,
        agent_uuid: selectedAgentId,
        success_metrics: selectedMetrics,
        settings: {
          description,
          start_option: scheduleStartOption,
          scheduled_start: scheduleStartOption === "scheduled" ? scheduledDateTime : undefined,
          timezone,
          calling_hours: {
            start_time: startTime,
            end_time: endTime,
          },
          active_days: activeDays,
          call_limits: {
            max_per_day: maxCallsPerDay,
            max_concurrent: maxConcurrentCalls,
          },
          retry_settings: {
            enabled: retryEnabled,
            max_attempts: maxAttempts,
            retry_interval_hours: Number((retryIntervalMinutes / 60).toFixed(3)),
          },
          agent_memory_enabled: memoryEnabled,
          campaign_level_memory_enabled: campaignLevelMemoryEnabled,
          previous_campaign_id: previousCampaignId,
        },
      };

      if (!createdCampaignId) {
        // Create new campaign as draft
        const campaign = await createCampaign(campaignData);
        setCreatedCampaignId(campaign.id);
        return campaign.id;
      } else {
        // Update existing campaign
        await updateCampaign(createdCampaignId, {
          name: campaignName,
          settings: campaignData.settings,
          success_metrics: selectedMetrics,
        });
        return createdCampaignId;
      }
    } catch (err: any) {
      throw new Error(err.message || "Failed to save campaign");
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Save campaign as draft when moving from step 1
      if (currentStep === 1) {
        await saveCampaignAsDraft();
      }

      // Update campaign settings when moving from step 2 (metrics)
      if (currentStep === 2 && createdCampaignId) {
        await saveCampaignAsDraft();
      }

      // Upload contacts when moving from step 3 (only if not already uploaded)
      if (currentStep === 3 && selectedFile && createdCampaignId && !contactsUploaded) {
        const uploadResponse = await uploadCampaignContacts(createdCampaignId, selectedFile);
        if (!uploadResponse.success) {
          throw new Error(uploadResponse.error || "Failed to upload contacts");
        }
        setContactsUploaded(true);
      }

      // Update campaign settings when moving from step 3
      if (currentStep === 4 && createdCampaignId) {
        await saveCampaignAsDraft();
      }

      setCurrentStep(currentStep + 1);
    } catch (err: any) {
      setError(err.message || "Failed to proceed");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateCurrentStep = () => {
    setError(null);

    switch (currentStep) {
      case 1:
        if (!campaignName.trim()) {
          setError("Campaign name is required");
          return false;
        }
        if (campaignName.length < 3 || campaignName.length > 100) {
          setError("Campaign name must be between 3 and 100 characters");
          return false;
        }
        if (!selectedAgentId) {
          setError("Please select an AI agent");
          return false;
        }
        return true;
      case 2:
        if (selectedMetrics.length === 0) {
          setError("Please select at least one success metric for your campaign");
          return false;
        }
        if (selectedMetrics.length > 20) {
          setError("Maximum 20 success metrics allowed per campaign");
          return false;
        }
        return true;
      case 3:
        if (!selectedFile) {
          setError("Please upload a CSV file with contacts");
          return false;
        }
        if (!uploadResult || !uploadResult.success) {
          setError("Please wait for file validation to complete");
          return false;
        }
        return true;
      case 4:
        if (scheduleStartOption === "scheduled" && !scheduledDateTime) {
          setError("Please select a date and time for scheduled start");
          return false;
        }
        if (scheduleStartOption === "scheduled") {
          const scheduledDate = new Date(scheduledDateTime);
          if (scheduledDate <= new Date()) {
            setError("Scheduled time must be in the future");
            return false;
          }
        }
        if (activeDays.length === 0) {
          setError("Please select at least one active day");
          return false;
        }
        if (maxCallsPerDay < 1 || maxCallsPerDay > 6000) {
          setError("Max calls per day must be between 1 and 1000");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setError("Please upload a CSV file");
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    setError(null);
    setContactsUploaded(false); // Reset upload state when new file is selected
    await validateCsvFile(file);
  };

  const validateCsvFile = async (file: File) => {
    setIsUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        setError("CSV file must contain at least a header row and one data row");
        setSelectedFile(null); // Clear file on validation error
        setUploadResult(null); // Clear upload result
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setIsUploading(false);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      if (!headers.includes('phone_number') && !headers.includes('phone')) {
        setError("CSV file must contain a 'phone_number' or 'phone' column");
        setSelectedFile(null); // Clear file on validation error
        setUploadResult(null); // Clear upload result
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setIsUploading(false);
        return;
      }

      if (!headers.includes('first_name') && !headers.includes('firstname') && !headers.includes('first name')) {
        setError("CSV file must contain a 'first_name' column");
        setSelectedFile(null); // Clear file on validation error
        setUploadResult(null); // Clear upload result
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setIsUploading(false);
        return;
      }

      // Parse first 10 rows for preview
      const preview: ContactPreview[] = [];
      const errors: string[] = [];

      for (let i = 1; i < Math.min(11, lines.length); i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const contact: ContactPreview = {
          phone_number: '',
          first_name: '',
        };

        headers.forEach((header, index) => {
          const value = values[index] || '';
          switch (header) {
            case 'phone_number':
            case 'phone':
              contact.phone_number = value;
              break;
            case 'first_name':
            case 'firstname':
              contact.first_name = value;
              break;
            case 'last_name':
            case 'lastname':
              contact.last_name = value;
              break;
            default:
              contact[header] = value;
              break;
          }
        });

        if (!contact.phone_number) {
          errors.push(`Row ${i + 1}: Missing phone number`);
        } else if (!contact.first_name) {
          errors.push(`Row ${i + 1}: Missing first name`);
        } else {
          preview.push(contact);
        }
      }

      setUploadResult({
        success: true,
        validContacts: lines.length - 1 - errors.length,
        errors,
        preview,
      });
    } catch (err) {
      setError("Failed to parse CSV file");
      setSelectedFile(null); // Clear file on parsing error
      setUploadResult(null); // Clear upload result
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Make sure campaign is saved with latest data
      const campaignId = createdCampaignId || await saveCampaignAsDraft();

      // Always start the campaign - the backend will determine if it should be RUNNING or SCHEDULED
      const startResponse = await startCampaign(campaignId);
      if (!startResponse.success) {
        throw new Error(startResponse.error || "Failed to launch campaign");
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to launch campaign");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setContactsUploaded(false); // Reset upload state when new file is selected via drag & drop
        validateCsvFile(file);
      } else {
        setError("Please upload a CSV file");
      }
    }
  };

  const handleCustomMetricSave = (metric: SuccessMetric) => {
    // Generate a temporary ID for the custom metric if it doesn't have one
    const metricWithId = {
      ...metric,
      id: metric.id || crypto.randomUUID()
    };

    // Check for duplicates using question text instead of ID for custom metrics
    const isDuplicate = customMetrics.some(m =>
      m.question.trim().toLowerCase() === metricWithId.question.trim().toLowerCase()
    );

    if (!isDuplicate) {
      setCustomMetrics(prev => [...prev, metricWithId]);
      setSelectedMetrics(prev => [...prev, metricWithId]);
    }

    setShowCustomMetricModal(false);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((step) => (
          <React.Fragment key={step}>
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${step <= currentStep
                ? "bg-accent text-primary-foreground"
                : "bg-gray-200 text-gray-400"
                }`}
            >
              {step}
            </div>
            {step < 5 && (
              <div
                className={`w-12 h-0.5 mx-2 ${step < currentStep ? "bg-primary" : "bg-gray-200"
                  }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-[var(--foreground)]">Campaign Basics</h3>
        <p className="text-[var(--foreground-muted)]">Set up your campaign details</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
          Campaign Name
        </label>
        <input
          type="text"
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          placeholder="Enter campaign name"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
          maxLength={100}
        />
        <div className="text-right text-sm text-gray-500 mt-1">
          {campaignName.length}/100
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
          How would you like to start?
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              value="scratch"
              checked={startOption === "scratch"}
              onChange={(e) => setStartOption(e.target.value)}
              className="text-primary focus:ring-primary"
            />
            <span className="ml-2">Start from scratch</span>
          </label>
          <label className="flex items-center text-gray-400">
            <input
              type="radio"
              disabled
              className="text-gray-400"
            />
            <span className="ml-2">Use a previous campaign</span>
          </label>
          <label className="flex items-center text-gray-400">
            <input
              type="radio"
              disabled
              className="text-gray-400"
            />
            <span className="ml-2">Use a team template</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
          Choose Your AI Agent
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {agents.filter(agent => agent.enabled && !agent.is_public).map((agent) => (
            <div
              key={agent.uuid}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedAgentId === agent.uuid
                ? "border-accent bg-accent/10"
                : "border-gray-300 hover:border-gray-400"
                }`}
              onClick={() => {
                setSelectedAgentId(agent.uuid);
                // Auto-select previously used metrics if available
                if (agent.selected_metrics && agent.selected_metrics.length > 0) {
                  setSelectedMetrics(agent.selected_metrics);
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-[var(--foreground)]">{agent.name}</h4>
                {agent.tags.includes("popular") && (
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                    Popular
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-2">{agent.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your campaign purpose and goals..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
          maxLength={200}
        />
        <div className="text-right text-sm text-gray-500 mt-1">
          {description.length}/200
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-[var(--foreground)]">Upload Contacts</h3>
        <p className="text-[var(--foreground-muted)]">Upload your contact list to start calling</p>
      </div>

      {!selectedFile ? (
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-accent transition-colors cursor-pointer"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            className="hidden"
          />
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <HiOutlineUpload className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-medium text-[var(--foreground)] mb-2">
            Drop your CSV file here or click to browse
          </h4>
          <p className="text-gray-600 mb-4">
            Maximum 50,000 contacts, up to 10MB
          </p>
          <button className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90">
            Browse Files
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {isUploading ? (
            <div className="text-center py-8">
              <LoadingState message="Validating CSV file..." />
            </div>
          ) : uploadResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <HiCheck className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-medium text-green-800">
                    {uploadResult.validContacts} valid contacts imported
                  </span>
                </div>
              </div>

              {uploadResult.preview.length > 0 && (
                <div>
                  <h4 className="font-medium text-[var(--foreground)] mb-3">
                    Contact Preview (first 10 rows)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Phone
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            First Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Last Name
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {uploadResult.preview.map((contact, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {contact.phone_number}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {contact.first_name || "-"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {contact.last_name || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {uploadResult.errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <HiExclamation className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="font-medium text-yellow-800">
                      {uploadResult.errors.length} rows have issues
                    </span>
                  </div>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {uploadResult.errors.length > 5 && (
                      <li>... and {uploadResult.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedFile(null);
                  setUploadResult(null);
                  setContactsUploaded(false); // Reset upload state when clearing file
                  setError(null); // Clear any existing errors
                  // Clear the file input
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="text-primary hover:text-primary/80 text-sm"
              >
                Upload Different File
              </button>
            </div>
          ) : null}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <HiDocumentText className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800 mb-1">CSV Format Requirements</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Required columns: phone_number, first_name</li>
              <li>• Optional columns: last_name</li>
              <li>• Additional columns will be stored as it is for agent's context</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper functions for metric styling - updated with new color palette
  const getPriorityColor = (priority: MetricPriority) => {
    switch (priority) {
      case MetricPriority.HIGH:
        return "text-red-600 bg-red-100"; // Keep red for high priority
      case MetricPriority.MEDIUM:
        return "text-orange-600 bg-orange-100"; // Use orange instead of yellow
      case MetricPriority.LOW:
        return "text-green-600 bg-green-100"; // Keep green for low priority
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getCategoryColor = (category: MetricCategory) => {
    switch (category) {
      case MetricCategory.SETUP:
        return "text-purple-700 bg-purple-100"; // Using new purple palette
      case MetricCategory.ENGAGEMENT:
        return "text-blue-700 bg-blue-100"; // Using new blue palette
      case MetricCategory.RESOLUTION:
        return "text-teal-700 bg-teal-100"; // Using new teal palette
      case MetricCategory.OUTCOME:
        return "text-green-700 bg-green-100"; // Using new green palette
      case MetricCategory.SATISFACTION:
        return "text-pink-700 bg-pink-100"; // Using new pink palette
      case MetricCategory.FOLLOW_UP:
        return "text-indigo-700 bg-indigo-100"; // Keep indigo for follow-up
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getAnswerTypeColor = (answerType: AnswerType) => {
    // Group similar answer types with similar colors from new palette
    if ([AnswerType.YES_NO, AnswerType.TRUE_FALSE, AnswerType.PRESENT_ABSENT].includes(answerType)) {
      return "text-green-700 bg-green-100"; // Using new green from palette
    }
    if ([AnswerType.SINGLE_SELECT, AnswerType.MULTI_SELECT].includes(answerType)) {
      return "text-blue-700 bg-blue-100"; // Using new blue from palette
    }
    if ([AnswerType.SCALE_5, AnswerType.SCALE_10, AnswerType.NPS, AnswerType.SENTIMENT].includes(answerType)) {
      return "text-purple-700 bg-purple-100"; // Using new purple from palette
    }
    if ([AnswerType.NUMBER, AnswerType.CURRENCY, AnswerType.PERCENTAGE, AnswerType.DURATION_IN_MINUTES].includes(answerType)) {
      return "text-pink-700 bg-pink-100"; // Using new pink from palette
    }
    if ([AnswerType.TEXT_SHORT, AnswerType.TEXT_LONG].includes(answerType)) {
      return "text-teal-700 bg-teal-100"; // Using new teal from palette
    }
    return "text-gray-600 bg-gray-100";
  };

  const getAnswerTypeLabel = (answerType: AnswerType) => {
    const labels: Record<AnswerType, string> = {
      [AnswerType.YES_NO]: "Yes/No",
      [AnswerType.TRUE_FALSE]: "True/False",
      [AnswerType.PRESENT_ABSENT]: "Present/Absent",
      [AnswerType.SINGLE_SELECT]: "Single Choice",
      [AnswerType.MULTI_SELECT]: "Multiple Choice",
      [AnswerType.SCALE_5]: "Scale (1-5)",
      [AnswerType.SCALE_10]: "Scale (1-10)",
      [AnswerType.NPS]: "NPS (0-10)",
      [AnswerType.SENTIMENT]: "Sentiment",
      [AnswerType.NUMBER]: "Number",
      [AnswerType.CURRENCY]: "Currency",
      [AnswerType.PERCENTAGE]: "Percentage",
      [AnswerType.DURATION_IN_MINUTES]: "Duration (minutes)",
      [AnswerType.TEXT_SHORT]: "Short Text",
      [AnswerType.TEXT_LONG]: "Long Text",
    };
    return labels[answerType] || answerType;
  };

  // Reusable MetricCard component
  const MetricCard = ({
    metric,
    isSelected,
    onToggle,
    showRemove = false,
    onRemove,
    variant = "ai"
  }: {
    metric: SuccessMetric;
    isSelected: boolean;
    onToggle: (metric: SuccessMetric) => void;
    showRemove?: boolean;
    onRemove?: (metric: SuccessMetric) => void;
    variant?: "ai" | "custom" | "standard";
  }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case "ai":
          return isSelected
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-gray-200 bg-white hover:border-gray-300';
        case "custom":
          return isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 bg-white hover:border-gray-300';
        case "standard":
          return isSelected
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-200 bg-white hover:border-gray-300';
        default:
          return isSelected
            ? 'border-gray-500 bg-gray-50'
            : 'border-gray-200 bg-white hover:border-gray-300';
      }
    };

    const getCheckboxStyles = () => {
      switch (variant) {
        case "ai":
          return isSelected
            ? 'border-emerald-500 bg-emerald-500'
            : 'border-gray-300';
        case "custom":
          return isSelected
            ? 'border-blue-500 bg-blue-500'
            : 'border-gray-300';
        case "standard":
          return isSelected
            ? 'border-purple-500 bg-purple-500'
            : 'border-gray-300';
        default:
          return isSelected
            ? 'border-gray-500 bg-gray-500'
            : 'border-gray-300';
      }
    };

    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer transition-all ${getVariantStyles()}`}
        onClick={() => onToggle(metric)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${getCheckboxStyles()}`}>
              {isSelected && (
                <HiCheck className="text-white text-xs" />
              )}
            </div>

            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">{metric.question}</h4>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(metric.priority)}`}>
                  {metric.priority.toUpperCase()}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAnswerTypeColor(metric.answer_type)}`}>
                  {getAnswerTypeLabel(metric.answer_type)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(metric.category)}`}>
                  {metric.category}
                </span>
              </div>
              <p className="text-sm text-gray-600">{metric.reason}</p>
              {metric.options && (
                <div className="mt-2">
                  <span className="text-xs text-gray-500">Options: </span>
                  <span className="text-xs text-gray-700">
                    {metric.options.slice(0, 3).join(", ")}
                    {metric.options.length > 3 && ` (+${metric.options.length - 3} more)`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {showRemove && onRemove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(metric);
              }}
              className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
              title="Remove custom question"
            >
              <HiTrash size={14} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const selectedAgent = agents.find(a => a.uuid === selectedAgentId);
    const suggestedMetrics = selectedAgent?.suggested_metrics || [];

    const toggleMetricSelection = (metric: SuccessMetric) => {
      setSelectedMetrics(prev => {
        const isSelected = prev.some(m =>
          m.question === metric.question &&
          m.answer_type === metric.answer_type &&
          m.category === metric.category
        );
        if (isSelected) {
          return prev.filter(m =>
            !(m.question === metric.question &&
              m.answer_type === metric.answer_type &&
              m.category === metric.category)
          );
        } else {
          return [...prev, metric];
        }
      });
    };

    const isMetricSelected = (metric: SuccessMetric) => {
      // For more precise matching, check multiple properties to distinguish between similar metrics
      return selectedMetrics.some(m =>
        m.question === metric.question &&
        m.answer_type === metric.answer_type &&
        m.category === metric.category
      );
    };

    // Get all selected metrics for the "All" tab
    const getAllSelectedMetrics = () => {
      // Always include all custom metrics first
      // Then include selected AI metrics
      const selectedAIMetrics = suggestedMetrics.filter(metric => isMetricSelected(metric));

      // Include selected standard metrics only if there's no custom metric with the same question
      const selectedStandardMetrics = standardMetrics.filter(metric =>
        isMetricSelected(metric) &&
        !customMetrics.some(customMetric =>
          customMetric.question === metric.question &&
          customMetric.answer_type === metric.answer_type &&
          customMetric.category === metric.category
        )
      );

      return [...customMetrics, ...selectedAIMetrics, ...selectedStandardMetrics];
    };

    // Render tab content based on active tab
    const renderTabContent = () => {
      switch (activeTab) {
        case "all":
          const selectedMetricsList = getAllSelectedMetrics();
          return (
            <div className="space-y-4">
              {/* Add Question Button at the top */}
              <div className="flex justify-center mb-4">
                <button
                  type="button"
                  onClick={() => setShowCustomMetricModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-dashed border-emerald-500 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                >
                  <HiPlus size={16} />
                  Add Custom Question
                </button>
              </div>

              {/* Selected Metrics */}
              {selectedMetricsList.length > 0 ? (
                <div className="space-y-3">
                  {selectedMetricsList.map((metric, index) => {
                    // Determine variant based on which array the metric belongs to
                    let variant: "ai" | "custom" | "standard" = "ai";

                    if (customMetrics.some(m => m.question === metric.question)) {
                      variant = "custom";
                    } else if (suggestedMetrics.some(m => m.question === metric.question)) {
                      variant = "ai";
                    } else if (standardMetrics.some(m => m.question === metric.question)) {
                      variant = "standard";
                    }

                    return (
                      <MetricCard
                        key={metric.id || `${metric.question}-${metric.answer_type}-${metric.category}`}
                        metric={metric}
                        isSelected={true}
                        onToggle={toggleMetricSelection}
                        showRemove={customMetrics.some(m => m.question === metric.question)}
                        onRemove={(metric) => {
                          setCustomMetrics(prev => prev.filter(m => m.question !== metric.question));
                          setSelectedMetrics(prev => prev.filter(m => m.question !== metric.question));
                        }}
                        variant={variant}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No metrics selected yet. Choose from AI Recommended or Standard tabs.</p>
                </div>
              )}
            </div>
          );

        case "ai-recommended":
          return (
            <div className="space-y-4">
              {suggestedMetrics.length > 0 ? (
                <div className="space-y-3">
                  {suggestedMetrics.map((metric, index) => (
                    <MetricCard
                      key={metric.id || `ai-${metric.question}-${metric.answer_type}-${metric.category}`}
                      metric={metric}
                      isSelected={isMetricSelected(metric)}
                      onToggle={toggleMetricSelection}
                      variant="ai"
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No AI recommended metrics available for this agent.</p>
                </div>
              )}
            </div>
          );

        case "standard":
          return (
            <div className="space-y-4">
              <div className="space-y-3">
                {standardMetrics.map((metric, index) => (
                  <MetricCard
                    key={metric.id || `standard-${metric.question}-${metric.answer_type}-${metric.category}`}
                    metric={metric}
                    isSelected={isMetricSelected(metric)}
                    onToggle={toggleMetricSelection}
                    variant="standard"
                  />
                ))}
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-[var(--foreground)]">Configure Success Metrics</h3>
          <p className="text-[var(--foreground-muted)]">Tell us what matters, and we'll track it automatically during calls</p>
        </div>

        {/* Validation Error - Moved to top for better visibility */}
        {selectedMetrics.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <HiExclamation className="text-red-600" size={16} />
              <span className="text-red-800 text-sm">
                Please select at least one success metric to continue
              </span>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("all")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "all"
                ? "border-emerald-500 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              All ({getAllSelectedMetrics().length})
            </button>
            <button
              onClick={() => setActiveTab("ai-recommended")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "ai-recommended"
                ? "border-emerald-500 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              AI Recommended ({suggestedMetrics.filter(m =>
                selectedMetrics.some(selected =>
                  selected.question === m.question &&
                  selected.answer_type === m.answer_type &&
                  selected.category === m.category &&
                  // Ensure it's actually an AI metric, not a custom one
                  !customMetrics.some(custom =>
                    custom.question === m.question &&
                    custom.answer_type === m.answer_type &&
                    custom.category === m.category
                  )
                )
              ).length}/{suggestedMetrics.length})
            </button>
            <button
              onClick={() => setActiveTab("standard")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "standard"
                ? "border-emerald-500 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Standard ({standardMetrics.filter(m =>
                selectedMetrics.some(selected =>
                  selected.question === m.question &&
                  selected.answer_type === m.answer_type &&
                  selected.category === m.category &&
                  // Ensure it's actually a standard metric, not a custom one
                  !customMetrics.some(custom =>
                    custom.question === m.question &&
                    custom.answer_type === m.answer_type &&
                    custom.category === m.category
                  )
                )
              ).length}/{standardMetrics.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {renderTabContent()}
        </div>

        {/* Selected Metrics Summary */}
        {selectedMetrics.length > 0 && (
          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-3">
              {selectedMetrics.length} question{selectedMetrics.length === 1 ? '' : 's'} selected • AI will analyze calls for these metrics
            </h4>
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => {
    const calculateEstimatedCompletion = () => {
      if (!uploadResult || maxCallsPerDay === 0) return "N/A";
      const totalContacts = uploadResult.validContacts;
      const daysNeeded = Math.ceil(totalContacts / maxCallsPerDay);
      const workingDaysPerWeek = activeDays.length;

      if (workingDaysPerWeek === 0) return "N/A";

      const weeksNeeded = Math.ceil(daysNeeded / workingDaysPerWeek);

      if (daysNeeded <= 1) return "1 day";
      if (daysNeeded <= 7) return `${daysNeeded} days`;
      return `~${weeksNeeded} week${weeksNeeded > 1 ? 's' : ''}`;
    };

    const isNonStandardHours = () => {
      const startHour = parseInt(startTime.split(':')[0]);
      const endHour = parseInt(endTime.split(':')[0]);
      return startHour < 8 || endHour > 20;
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-[var(--foreground)]">Campaign Schedule</h3>
          <p className="text-[var(--foreground-muted)]">Configure when and how your campaign runs</p>
        </div>

        {/* Start Options */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
            When should this campaign start?
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="immediate"
                checked={scheduleStartOption === "immediate"}
                onChange={(e) => setScheduleStartOption(e.target.value as "immediate")}
                className="text-primary focus:ring-primary"
              />
              <span className="ml-2">Start immediately when launched</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="scheduled"
                checked={scheduleStartOption === "scheduled"}
                onChange={(e) => setScheduleStartOption(e.target.value as "scheduled")}
                className="text-primary focus:ring-primary"
              />
              <span className="ml-2">Schedule for a specific date and time</span>
            </label>
          </div>

          {scheduleStartOption === "scheduled" && (
            <div className="mt-3 ml-6">
              <input
                type="datetime-local"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent]"
              />
            </div>
          )}
        </div>

        {/* Calling Hours */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
            Calling Hours
          </label>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Time</label>
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-background"
              >
                {TIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Time</label>
              <select
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-background"
              >
                {TIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-background"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>

          {isNonStandardHours() && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <HiExclamation className="inline w-4 h-4 mr-1" />
                Calling outside 8 AM - 8 PM may have lower answer rates
              </p>
            </div>
          )}
        </div>

        {/* Active Days */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
            Active Days
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day}
                onClick={() => {
                  setActiveDays(prev =>
                    prev.includes(day)
                      ? prev.filter(d => d !== day)
                      : [...prev, day]
                  );
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${activeDays.includes(day)
                  ? "bg-background text-primary"
                  : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                  }`}
              >
                {day}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveDays(["Mon", "Tue", "Wed", "Thu", "Fri"])}
              className="text-sm text-primary hover:text-primary/80"
            >
              Weekdays only
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => setActiveDays(DAYS_OF_WEEK)}
              className="text-sm text-primary hover:text-primary/80"
            >
              All days
            </button>
          </div>
        </div>

        {/* Call Limits */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-3">
            Call Limits
          </label>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Max calls per day</span>
                <span className="font-medium">{maxCallsPerDay}</span>
              </div>
              <input
                type="range"
                min="1"
                max="1000"
                value={maxCallsPerDay}
                onChange={(e) => setMaxCallsPerDay(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent"
              />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Max concurrent calls</span>
                <span className="font-medium">{maxConcurrentCalls}</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={maxConcurrentCalls}
                onChange={(e) => setMaxConcurrentCalls(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent"
              />
            </div>
          </div>

          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Estimated completion:</strong> {calculateEstimatedCompletion()}
              {uploadResult && ` (${uploadResult.validContacts} contacts)`}
            </p>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="border-t pt-4">
          <button
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="flex items-center text-sm font-medium text-[var(--foreground)] hover:text-[var(--foreground)]/80"
          >
            <HiChevronRight className={`w-4 h-4 mr-1 transition-transform ${showAdvancedSettings ? 'rotate-90' : ''}`} />
            Advanced Settings
          </button>

          {showAdvancedSettings && (
            <div className="mt-4 space-y-4 pl-5">
              <div className="flex items-center justify-between">
                <label className="text-sm text-[var(--foreground)]">Retry failed calls</label>
                <button
                  onClick={() => setRetryEnabled(!retryEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${retryEnabled ? 'bg-gray-200' : 'bg-gray-200'
                    } transition-colors`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-secondary transition-transform ${retryEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {retryEnabled && (
                <>
                  <div>
                    <label className="block text-sm text-[var(--foreground)] mb-1">
                      Max attempts per contact
                    </label>
                    <select
                      value={maxAttempts}
                      onChange={(e) => setMaxAttempts(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-background"
                    >
                      {[1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-[var(--foreground)] mb-1">
                      Time between retries (minutes)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      value={retryIntervalMinutes}
                      onChange={(e) => setRetryIntervalMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-background"
                      placeholder="Enter minutes (e.g., 30, 60, 120)"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Agent Memory */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--foreground)]">
                Enable agent memory
              </p>
              <p className="text-xs text-[var(--foreground-muted)]">
                When enabled, the agent will remember previous conversations for this campaign.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMemoryEnabled(!memoryEnabled)}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-secondary transition-transform ${memoryEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
              />
            </button>
          </div>
        </div>

        {/* Campaign-Level Memory */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className={`text-sm font-medium ${memoryEnabled ? 'text-[var(--foreground)]' : 'text-gray-400'}`}>
                Continue call from last campaign
              </p>
              <p className={`text-xs ${memoryEnabled ? 'text-[var(--foreground-muted)]' : 'text-gray-400'}`}>
                Load per-contact memory from a previous campaign to continue conversations.
              </p>
              {!memoryEnabled && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  ⚠️ Enable agent memory first to use this feature
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={!memoryEnabled}
              onClick={() => {
                if (memoryEnabled) {
                  setCampaignLevelMemoryEnabled(!campaignLevelMemoryEnabled);
                  if (!campaignLevelMemoryEnabled) {
                    setPreviousCampaignId(null);
                  }
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${memoryEnabled ? 'bg-gray-200 cursor-pointer' : 'bg-gray-100 cursor-not-allowed opacity-50'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full transition-transform ${campaignLevelMemoryEnabled && memoryEnabled ? 'translate-x-6 bg-secondary' : 'translate-x-1 bg-gray-300'
                  }`}
              />
            </button>
          </div>

          {campaignLevelMemoryEnabled && memoryEnabled && (
            <div className="mt-4">
              <label className="block text-sm text-[var(--foreground)] mb-2">
                Select previous campaign
              </label>
              <div className="relative" ref={campaignDropdownRef}>
                <div
                  className={`flex items-center w-full px-3 py-2 border rounded-lg bg-background ${isCampaignDropdownOpen ? 'ring-2 ring-accent border-transparent' : 'border-gray-300'}`}
                >
                  <HiSearch className="text-gray-400 mr-2" />
                  <input
                    type="text"
                    value={isCampaignDropdownOpen ? campaignSearchTerm : (availableCampaigns.find(c => c.id === previousCampaignId)?.name || "")}
                    placeholder="Search campaigns by name or ID..."
                    onFocus={() => setIsCampaignDropdownOpen(true)}
                    onChange={(e) => {
                      setCampaignSearchTerm(e.target.value);
                      setIsCampaignDropdownOpen(true);
                    }}
                    className="flex-1 bg-transparent border-none focus:ring-0 text-sm p-0"
                  />
                  {previousCampaignId && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviousCampaignId(null);
                        setCampaignSearchTerm("");
                        setIsCampaignDropdownOpen(false);
                      }}
                      className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Clear selection"
                    >
                      <HiX className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsCampaignDropdownOpen(!isCampaignDropdownOpen)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <HiChevronDown className={`w-5 h-5 transition-transform ${isCampaignDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {isCampaignDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-background border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {loadingCampaigns ? (
                      <div className="px-4 py-2 text-sm text-gray-500">Loading campaigns...</div>
                    ) : (
                      <>
                        {availableCampaigns
                          .filter((c) => c.id !== createdCampaignId)
                          .filter((c) =>
                            c.name.toLowerCase().includes(campaignSearchTerm.toLowerCase()) ||
                            c.id.toLowerCase().includes(campaignSearchTerm.toLowerCase())
                          )
                          .length > 0 ? (
                          availableCampaigns
                            .filter((c) => c.id !== createdCampaignId)
                            .filter((c) =>
                              c.name.toLowerCase().includes(campaignSearchTerm.toLowerCase()) ||
                              c.id.toLowerCase().includes(campaignSearchTerm.toLowerCase())
                            )
                            .map((campaign) => (
                              <div
                                key={campaign.id}
                                onClick={() => {
                                  setPreviousCampaignId(campaign.id);
                                  setCampaignSearchTerm("");
                                  setIsCampaignDropdownOpen(false);
                                }}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-accent/10 flex justify-between items-center ${previousCampaignId === campaign.id ? 'bg-accent/5 text-accent font-medium' : 'text-gray-700'}`}
                              >
                                <span>{campaign.name}</span>
                                <span className="text-xs text-gray-400">ID: {campaign.id.substring(0, 8)}...</span>
                              </div>
                            ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500 italic">No matching campaigns found</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {availableCampaigns.length === 0 && !loadingCampaigns && (
                <p className="text-sm text-gray-500 mt-2">No campaigns available to inherit memory from.</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-[var(--foreground)]">Review & Launch</h3>
        <p className="text-[var(--foreground-muted)]">Review your campaign details before launching</p>
      </div>

      <div className="bg-background rounded-lg p-6 space-y-4">
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Campaign:</span>
          <span className="text-[var(--foreground)]">{campaignName}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Agent:</span>
          <span className="text-[var(--foreground)]">
            {agents.find(a => a.uuid === selectedAgentId)?.name}
          </span>
        </div>
        <div className="flex justify-between items-start">
          <span className="font-medium text-[var(--foreground-muted)]">Success Metrics:</span>
          <div className="text-right max-w-md">
            <span className="text-[var(--foreground)]">
              {selectedMetrics.length} metric{selectedMetrics.length === 1 ? '' : 's'} selected
            </span>
            <div className="text-xs text-[var(--foreground-muted)] mt-1">
              {selectedMetrics.filter(m => m.priority === MetricPriority.HIGH).length} high, {' '}
              {selectedMetrics.filter(m => m.priority === MetricPriority.MEDIUM).length} medium, {' '}
              {selectedMetrics.filter(m => m.priority === MetricPriority.LOW).length} low priority
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Contacts:</span>
          <span className="text-[var(--foreground)]">
            {uploadResult?.validContacts || 0}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Start:</span>
          <span className="text-[var(--foreground)]">
            {scheduleStartOption === "immediate"
              ? "Immediately when launched"
              : new Date(scheduledDateTime).toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Calling Hours:</span>
          <span className="text-[var(--foreground)]">
            {TIME_OPTIONS.find(opt => opt.value === startTime)?.label} - {TIME_OPTIONS.find(opt => opt.value === endTime)?.label}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Active Days:</span>
          <span className="text-[var(--foreground)]">
            {activeDays.join(", ")}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Daily Call Limit:</span>
          <span className="text-[var(--foreground)]">
            {maxCallsPerDay} calls/day
          </span>
        </div>
        {retryEnabled && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-[var(--foreground-muted)]">Retry Settings:</span>
            <span className="text-[var(--foreground)]">
              Up to {maxAttempts} attempts, {retryIntervalMinutes} min between
            </span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="font-medium text-[var(--foreground-muted)]">Agent Memory:</span>
          <span className="text-[var(--foreground)]">
            {memoryEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>
        {campaignLevelMemoryEnabled && previousCampaignId && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-[var(--foreground-muted)]">Continuing from campaign:</span>
            <span className="text-[var(--foreground)]">
              {availableCampaigns.find(c => c.id === previousCampaignId)?.name || previousCampaignId}
            </span>
          </div>
        )}
        {description && (
          <div className="flex justify-between items-start">
            <span className="font-medium text-[var(--foreground-muted)]">Description:</span>
            <span className="text-[var(--foreground)] text-right max-w-xs">{description}</span>
          </div>
        )}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <HiCheck className="w-5 h-5 text-green-600 mr-2" />
          <div>
            <span className="font-medium text-green-800">This campaign complies with TCPA regulations</span>
            <p className="text-sm text-green-700 mt-1">
              Your settings meet all regulatory requirements
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {renderStepIndicator()}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="min-h-[400px]">
        {isLoading ? (
          <LoadingState message={currentStep === 5 ? "Launching campaign..." : "Saving progress..."} />
        ) : (
          <>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep3()}
            {currentStep === 3 && renderStep2()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
          </>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <div className="flex space-x-2">
          {currentStep > 1 && (
            <button
              onClick={handlePrevious}
              className="flex items-center px-4 py-2 text-[var(--foreground)] bg-[var(--background)] border border-[var(--border)] rounded-lg hover:bg-[var(--muted)]"
              disabled={isLoading}
            >
              <HiChevronLeft className="w-4 h-4 mr-1" />
              Back
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-4 py-2 text-[var(--foreground)] bg-[var(--background)] border border-[var(--border)] rounded-lg hover:bg-[var(--muted)]"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>

        <div>
          {currentStep < 5 ? (
            <button
              onClick={handleNext}
              className="flex items-center px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || isUploading}
            >
              {currentStep === 1 ? "Next: Configure Metrics" :
                currentStep === 2 ? "Next: Upload Contacts" :
                  currentStep === 3 ? "Next: Set Schedule" :
                    "Next: Review"}
              <HiChevronRight className="w-4 h-4 ml-1" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Launch Campaign
            </button>
          )}
        </div>
      </div>

      {/* Custom Metric Modal */}
      <CustomMetricModal
        isOpen={showCustomMetricModal}
        onClose={() => setShowCustomMetricModal(false)}
        onSave={handleCustomMetricSave}
      />
    </div>
  );
};

export default CreateCampaign;
